import NODE_TYPES from '../types';
import { getModel } from './model';

/**
 * Find a node in the machete segmentation tree
 * @param {String} uuid - Machete node uuid
 */
export const findNodeByUUID = (uuid) => {
  let found = null;
  getModel().segmentationTree.traverse((n) => {
    if (!found && n.uuid === uuid) found = n;
  }, true);
  return found;
};

/**
 * Find node in the machete segmentation tree
 * @param {Object} nodeRef - returns the nodeRef
 * @param {String} nodeRef - finds the node with this uuid
 * @returns {Object} A machete segmentation node
 */
export const getNode = (nodeRef) => {
  let node = nodeRef;
  if (typeof nodeRef === 'string') {
    node = findNodeByUUID(nodeRef);
  }
  if (!node) throw new Error('Must pass a node instance or uuid');
  return node;
};

// Operate on node
export const updateNodeProperties = (nodeRef, props) => {
  const node = getNode(nodeRef);
  Object.entries(props).forEach(([key, value]) => {
    const val =
      typeof value === 'function'
        ? value(node.uuid, node.getProperty(key))
        : value;
    getModel().segmentationTree.performOperation(node, key, val);
  });
};

// Traverse Tree
export const traverseMap = (nodeRef, cb, deep = false) => {
  const node = getNode(nodeRef);
  const result = [];
  node.traverse((n) => result.push(cb(n)), deep);
  return result;
};

export const traverseFilter = (nodeRef, cb, deep = false) => {
  const node = getNode(nodeRef);
  const result = [];
  node.traverse((n) => cb(n) && result.push(n), deep);
  return result;
};

export const traverseReduce = (nodeRef, cb, acc, deep = false) => {
  const node = getNode(nodeRef);
  let tmp = acc;
  node.traverse((n) => {
    tmp = cb(tmp, n);
  }, deep);
  return tmp;
};

/**
 * Find children of a given type within a segmentation tree
 * @param {Object} nodeRef - Reference to a machete node
 * @param {String} nodeRef - A node uuid
 * @param {String} type - Type of child to search for
 * @param {Object} options
 * @param {Boolean} options.deep recursive?
 * @param {Boolean} options.inclusive include the root node?
 * @returns {Array} Array of machete segmentation nodes
 */
export const getChildrenByType = (
  nodeRef,
  type,
  { deep = false, inclusive = true } = {},
) => {
  const node = getNode(nodeRef);
  const types = Array.isArray(type) ? type : [type];
  const children = traverseFilter(node, (n) => types.includes(n.type), deep);
  if (!inclusive) children.shift();
  return children;
};

export const findChildByType = (...args) => getChildrenByType(...args)[0];

export const findAndGroupChildrenByType = (
  nodeRef,
  type,
  { deep = false } = {},
) =>
  getChildrenByType(nodeRef, type, { deep }).reduce((acc, n) => {
    if (!acc[n.type]) acc[n.type] = [];
    acc[n.type].push(n);
    return acc;
  }, {});

/**
 * Gets the parent of a given model element
 * @param {Object} element - A machete element
 * @param {String} type - Type of parent element to search for
 * @returns {Object} A machete segmentation node
 */
export const getParentForElement = (element, type = 'parent') => {
  if (element.type === type) return element;
  if (element.parent.type === type) return element.parent;
  return getParentForElement(element.parent, type);
};

// Add / Remove Nodes

export const addSplitLevelTo = (nodeRef) => {
  const splitNode = getNode(nodeRef);
  const tree = getModel().segmentationTree;
  const decoratorNames = tree.defaultDecorators;
  // Return if the split already has wall node children
  if (decoratorNames.length === splitNode.children.length) return;
  const wallNodes = decoratorNames.map((decoratorName) =>
    tree.createNode(splitNode.modelElements, decoratorName),
  );
  wallNodes.forEach((wallNode) => tree.addNodeTo(wallNode, splitNode));
};

export const removeNode = (nodeRef) => {
  const node = getNode(nodeRef);
  getModel().segmentationTree.removeNode(node);
};

// Map Segmentation Tree properties to Redux store properties

const propertiesArrayToPropertiesMap = (properties) =>
  properties.reduce((acc, val) => {
    acc[val.type] = {
      name: val.name,
      options: val.options,
      range: val.range,
      step: val.step,
      type: val.type,
      value: val.value,
    };
    return acc;
  }, {});

// Get info about nodes

const getNodeDepth = (node) => {
  let depth = 0;
  let n = node;
  while (n.parent) {
    depth += 1;
    n = n.parent;
  }
  return depth;
};

// Map Segmentation Node to redux object

const getStateForNode = (nodeRef) => {
  const node = getNode(nodeRef);
  const area = [NODE_TYPES.SPLIT, NODE_TYPES.PARENT].includes(node.type)
    ? node.originalModelElements[0].area
    : null;
  return {
    uuid: node.uuid,
    depth: getNodeDepth(node),
    properties: propertiesArrayToPropertiesMap(node.properties),
    area,
    type: node.type,
    parentUUID: node.parent && node.parent.uuid,
    childrenByType: node.children.reduce((acc, n) => {
      acc[n.type] = acc[n.type] || {};
      acc[n.type][n.uuid] = true;
      return acc;
    }, {}),
  };
};

// Get flat representation of Segmentation Tree

const getNodeMap = (nodeRef) => {
  const node = nodeRef != null ? getNode(nodeRef) : getModel().segmentationTree;
  return traverseMap(node, (n) => getStateForNode(n), true).reduce((acc, n) => {
    acc[n.uuid] = n;
    return acc;
  }, {});
};

// Create node hierarchy for window

export const createWindowPackage = (parentId, properties) => {
  // create the window package node heirarchy
  const { segmentationTree } = getModel();
  const windowPackage = Hover.Machete.SegmentationUtils.createWindowPackageNodeHierarchy(
    segmentationTree,
    {
      ...properties,
      sash: true,
      elements: [getNode(parentId).originalModelElements[0]],
    },
  );
  console.log('window package in utils.createWindowPackage', windowPackage);

  // add the window package to the facade node
  const facadeSegmentationNode = getNode(parentId);
  segmentationTree.addNodeTo(windowPackage, facadeSegmentationNode);

  // update window package to be present
  updateNodeProperties(windowPackage, { present: true });

  // find the window opening node
  const openings = getChildrenByType(windowPackage, NODE_TYPES.WINDOW_OPENING, {
    deep: true,
  });

  const shutters = getChildrenByType(
    windowPackage,
    [NODE_TYPES.LEFT_SHUTTER, NODE_TYPES.RIGHT_SHUTTER],
    { deep: true },
  );

  // update its width, height, x, y
  updateNodeProperties(openings[0], properties);
  updateNodeProperties(shutters[0], {
    x: 0.5,
    y: 0.5,
    width: 12,
    height: 12,
  });

  return windowPackage.uuid;
};

/*
 * @param {Object} node
 * @return {boolean}
 */
const isNodePickable = ({ type, properties }) =>
  type === 'parent' || (properties.present && properties.present.value);

export default {
  findNodeByUUID,
  getNode,
  updateNodeProperties,
  traverseMap,
  traverseFilter,
  traverseReduce,
  getChildrenByType,
  findChildByType,
  findAndGroupChildrenByType,
  getParentForElement,
  addSplitLevelTo,
  removeNode,
  getStateForNode,
  getNodeMap,
  createWindowPackage,
  propertiesArrayToPropertiesMap,
  isNodePickable,
};
